import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { XMarkIcon, CheckCircleIcon, ExclamationCircleIcon, ClockIcon, NoSymbolIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const STATUS = {
  PENDING: 'pending',
  REFRESHING: 'refreshing',
  SUCCESS: 'success',
  FAILED: 'failed',
  SKIPPED: 'skipped',
};

const RefreshEcommerceScriptsModal = ({ isOpen, onClose }) => {
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [siteStatuses, setSiteStatuses] = useState({});
  const cancelledRef = useRef(false);
  const currentIdRef = useRef(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/websites/ecommerce-refresh-list`);
      setWebsites(res.data.websites || []);
      const initial = {};
      (res.data.websites || []).forEach(w => {
        initial[w.id] = w.is_trial_expired ? STATUS.SKIPPED : STATUS.PENDING;
      });
      setSiteStatuses(initial);
    } catch {
      setWebsites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      cancelledRef.current = false;
      setDone(false);
      setRunning(false);
      fetchList();
    }
  }, [isOpen, fetchList]);

  const eligible = websites.filter(w => !w.is_trial_expired);
  const skipped = websites.filter(w => w.is_trial_expired);
  const successCount = Object.values(siteStatuses).filter(s => s === STATUS.SUCCESS).length;
  const failCount = Object.values(siteStatuses).filter(s => s === STATUS.FAILED).length;

  const handleStart = async () => {
    setRunning(true);
    setDone(false);
    cancelledRef.current = false;

    for (const website of eligible) {
      if (cancelledRef.current) break;

      currentIdRef.current = website.id;
      setSiteStatuses(prev => ({ ...prev, [website.id]: STATUS.REFRESHING }));

      try {
        await axios.post(`${API_BASE_URL}/api/admin/websites/${website.id}/refresh-scripts`);
        setSiteStatuses(prev => ({ ...prev, [website.id]: STATUS.SUCCESS }));
      } catch {
        setSiteStatuses(prev => ({ ...prev, [website.id]: STATUS.FAILED }));
      }
    }

    currentIdRef.current = null;
    setRunning(false);
    setDone(true);
  };

  const handleCancel = () => {
    cancelledRef.current = true;
  };

  const handleClose = () => {
    if (running) {
      cancelledRef.current = true;
    }
    onClose();
  };

  if (!isOpen) return null;

  const statusIcon = (status) => {
    switch (status) {
      case STATUS.SUCCESS:
        return <CheckCircleIcon className="w-5 h-5 text-green-500 flex-shrink-0" />;
      case STATUS.FAILED:
        return <ExclamationCircleIcon className="w-5 h-5 text-red-500 flex-shrink-0" />;
      case STATUS.REFRESHING:
        return (
          <svg className="w-5 h-5 text-blue-500 animate-spin flex-shrink-0" viewBox="0 0 24 24" fill="none">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        );
      case STATUS.SKIPPED:
        return <NoSymbolIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-300 flex-shrink-0" />;
    }
  };

  const statusLabel = (status) => {
    switch (status) {
      case STATUS.SUCCESS: return 'Done';
      case STATUS.FAILED: return 'Failed';
      case STATUS.REFRESHING: return 'Refreshing...';
      case STATUS.SKIPPED: return 'Skipped';
      default: return 'Pending';
    }
  };

  const modeLabel = (mode) => {
    if (mode === 'catalog') return 'Catalog';
    if (mode === 'courses') return 'Courses';
    return 'Full E-comm';
  };

  const modeBadgeClass = (mode) => {
    if (mode === 'catalog') return 'bg-purple-100 text-purple-700';
    if (mode === 'courses') return 'bg-indigo-100 text-indigo-700';
    return 'bg-amber-100 text-amber-700';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={handleClose} />

        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-full max-w-2xl">
          {/* Header */}
          <div className="bg-white px-6 pt-5 pb-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Refresh E-commerce Scripts</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Re-apply latest JS enhancements and V2 CSS to all e-commerce sites
                </p>
              </div>
              <button onClick={handleClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Summary bar */}
          {!loading && (
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-4 text-sm">
              <span className="text-gray-700 font-medium">{websites.length} sites found</span>
              <span className="text-gray-400">|</span>
              <span className="text-blue-600">{eligible.length} eligible</span>
              {skipped.length > 0 && (
                <>
                  <span className="text-gray-400">|</span>
                  <span className="text-gray-500">{skipped.length} skipped (expired trial)</span>
                </>
              )}
              {done && (
                <>
                  <span className="text-gray-400">|</span>
                  <span className="text-green-600">{successCount} succeeded</span>
                  {failCount > 0 && (
                    <>
                      <span className="text-gray-400">|</span>
                      <span className="text-red-600">{failCount} failed</span>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* Progress bar */}
          {running && eligible.length > 0 && (
            <div className="w-full bg-gray-200 h-1">
              <div
                className="bg-blue-500 h-1 transition-all duration-300"
                style={{
                  width: `${((successCount + failCount) / eligible.length) * 100}%`
                }}
              />
            </div>
          )}

          {/* Site list */}
          <div className="px-6 py-4 max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="w-8 h-8 text-gray-300 animate-spin" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="ml-3 text-gray-500">Loading e-commerce websites...</span>
              </div>
            ) : websites.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No e-commerce websites found.</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b">
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2 font-medium">Website</th>
                    <th className="pb-2 font-medium">Owner</th>
                    <th className="pb-2 font-medium">Mode</th>
                    <th className="pb-2 font-medium">Design</th>
                    <th className="pb-2 font-medium">Account</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {websites.map(w => {
                    const status = siteStatuses[w.id] || STATUS.PENDING;
                    const isActive = status === STATUS.REFRESHING;
                    return (
                      <tr
                        key={w.id}
                        className={`${isActive ? 'bg-blue-50' : ''} ${status === STATUS.SKIPPED ? 'opacity-50' : ''}`}
                      >
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-1.5">
                            {statusIcon(status)}
                            <span className={`text-xs font-medium ${
                              status === STATUS.SUCCESS ? 'text-green-600' :
                              status === STATUS.FAILED ? 'text-red-600' :
                              status === STATUS.REFRESHING ? 'text-blue-600' :
                              status === STATUS.SKIPPED ? 'text-gray-400' :
                              'text-gray-400'
                            }`}>
                              {statusLabel(status)}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="font-medium text-gray-900 truncate block max-w-[180px]" title={w.name}>
                            {w.name}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className="text-gray-600 truncate block max-w-[140px]" title={w.owner_email}>
                            {w.owner_name || w.owner_email || '—'}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${
                            modeBadgeClass(w.ecommerce_mode)
                          }`}>
                            {modeLabel(w.ecommerce_mode)}
                          </span>
                        </td>
                        <td className="py-2 pr-3">
                          <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-semibold rounded ${
                            w.is_v2 ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {w.is_v2 ? 'V2' : 'V1'}
                          </span>
                        </td>
                        <td className="py-2">
                          {w.is_trial_expired ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded bg-red-100 text-red-700">
                              Trial expired
                            </span>
                          ) : (
                            <span className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded ${
                              w.account_tier === 'trial' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {(w.account_tier || 'free').charAt(0).toUpperCase() + (w.account_tier || 'free').slice(1)}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              {done && cancelledRef.current && 'Cancelled by user.'}
              {done && !cancelledRef.current && `Completed. ${successCount} of ${eligible.length} refreshed successfully.`}
            </div>
            <div className="flex items-center gap-3">
              {running ? (
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              ) : (
                <>
                  <button
                    onClick={handleClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-lg transition-colors"
                  >
                    {done ? 'Close' : 'Cancel'}
                  </button>
                  {!done && eligible.length > 0 && (
                    <button
                      onClick={handleStart}
                      disabled={loading}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Refresh {eligible.length} site{eligible.length !== 1 ? 's' : ''}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefreshEcommerceScriptsModal;
